import { useMutation } from '@apollo/react-hooks';
import { UPDATE_ARTICLES_STARRED_MUTATION, kmsClient } from 'api';
import {
  UpdateArticlesStarredMutation,
  UpdateArticlesStarredMutation_updateArticlesStarred_articles,
  UpdateArticlesStarredMutationVariables
} from './__generated__/UpdateArticlesStarredMutation';

type MutationProps = {
  onStart?: (variables: UpdateArticlesStarredMutationVariables) => void;
  onCompleted?: (
    articles: UpdateArticlesStarredMutation_updateArticlesStarred_articles[],
    variables: UpdateArticlesStarredMutationVariables
  ) => void;
  onError?: (errors: string[], variables: UpdateArticlesStarredMutationVariables) => void;
};

// Each star click is answered with the variables it was sent with, so a caller that has several
// clicks in flight rolls back and reports the request that actually failed, not the oldest one.
export const useUpdateArticlesStarred = ({
  onStart,
  onCompleted,
  onError
}: MutationProps): [(variables: UpdateArticlesStarredMutationVariables) => void, { loading: boolean }] => {
  const [updateArticlesStarred, { loading }] = useMutation<
    UpdateArticlesStarredMutation,
    UpdateArticlesStarredMutationVariables
  >(UPDATE_ARTICLES_STARRED_MUTATION, { client: kmsClient });

  return [
    (variables: UpdateArticlesStarredMutationVariables) => {
      if (onStart) onStart(variables);

      updateArticlesStarred({ variables })
        .then(({ data }) => {
          const payload = data?.updateArticlesStarred;

          if (payload?.errors?.length) {
            if (onError) onError(payload.errors, variables);
            return;
          }
          // An empty list means nothing was saved, so it must not pass as success: the caller has
          // already flipped the star optimistically and would leave it flipped.
          if (!payload?.articles?.length) {
            if (onError) onError(['No articles were updated.'], variables);
            return;
          }

          if (onCompleted) onCompleted(payload.articles, variables);
        })
        .catch((error) => onError && onError([error.message], variables));
    },
    { loading }
  ];
};
